import { useState, useCallback, useEffect } from 'react';
import { processContact, deduplicateContacts } from '../../helpers/contactUtils';

/**
 * Hook for importing contacts from Google Contacts API
 * Uses Google Identity Services (GIS) - the new OAuth 2.0 library
 * Works on all platforms including iOS
 * 
 * @param {string} googleClientId - Google OAuth Client ID from Google Cloud Console
 * @returns {Object} - Google Contacts functionality and state
 */
const useGoogleContacts = (googleClientId) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [accessToken, setAccessToken] = useState(null);

    // Load Google Identity Services and API scripts
    useEffect(() => {
        if (!googleClientId) {
            return;
        }

        // Check if already loaded
        if (window.google && window.gapi && window.gapi.client) {
            setIsApiLoaded(true);
            return;
        }

        let gisScriptLoaded = false;
        let apiScriptLoaded = false;

        const checkBothLoaded = () => {
            if (gisScriptLoaded && apiScriptLoaded) {
                // Initialize gapi client
                window.gapi.load('client', async () => {
                    try {
                        await window.gapi.client.init({});
                        setIsApiLoaded(true);
                    } catch (err) {
                        console.error('Error initializing gapi client:', err);
                        setError('Failed to initialize Google API client');
                    }
                });
            }
        };

        // Load Google Identity Services (new OAuth library)
        const gisScript = document.createElement('script');
        gisScript.src = 'https://accounts.google.com/gsi/client';
        gisScript.async = true;
        gisScript.defer = true;
        gisScript.onload = () => {
            gisScriptLoaded = true;
            checkBothLoaded();
        };
        gisScript.onerror = () => {
            setError('Failed to load Google Identity Services');
        };
        document.body.appendChild(gisScript);

        // Load Google API client library (for People API)
        const apiScript = document.createElement('script');
        apiScript.src = 'https://apis.google.com/js/api.js';
        apiScript.async = true;
        apiScript.defer = true;
        apiScript.onload = () => {
            apiScriptLoaded = true;
            checkBothLoaded();
        };
        apiScript.onerror = () => {
            setError('Failed to load Google API script');
        };
        document.body.appendChild(apiScript);

        return () => {
            // Cleanup if needed
        };
    }, [googleClientId]);

    /**
     * Authenticates user with Google using new Identity Services and fetches contacts
     * @returns {Promise<Array>} - Array of processed contacts
     */
    const importGoogleContacts = useCallback(async () => {
        if (!googleClientId) {
            throw new Error('Google Client ID not configured. Please contact support.');
        }

        if (!window.google || !window.gapi) {
            throw new Error('Google API is still loading. Please wait a moment and try again.');
        }

        setIsLoading(true);
        setError(null);

        try {
            // Step 1: Get access token using Google Identity Services (new method)
            const tokenResponse = await new Promise((resolve, reject) => {
                if (!window.google?.accounts?.oauth2) {
                    reject(new Error('Google Identity Services not loaded'));
                    return;
                }

                let tokenClient = null;
                
                tokenClient = window.google.accounts.oauth2.initTokenClient({
                    client_id: googleClientId,
                    scope: 'https://www.googleapis.com/auth/contacts.readonly',
                    callback: (response) => {
                        if (response.error) {
                            reject(new Error(response.error));
                        } else if (response.access_token) {
                            resolve(response);
                        } else {
                            reject(new Error('No access token received'));
                        }
                    },
                    error_callback: (error) => {
                        reject(new Error(error || 'Authentication failed'));
                    }
                });
                
                // Request the access token (this opens the OAuth popup)
                tokenClient.requestAccessToken({ prompt: 'consent' });
            });

            if (!tokenResponse || !tokenResponse.access_token) {
                setIsLoading(false);
                return [];
            }

            const token = tokenResponse.access_token;
            setAccessToken(token);
            setIsAuthenticated(true);

            // Step 2: Initialize gapi client with the token
            if (!window.gapi.client) {
                await window.gapi.client.init({});
            }

            // Set the access token for API calls
            window.gapi.client.setToken({ access_token: token });

            // Step 3: Load People API
            await window.gapi.client.load('https://people.googleapis.com/$discovery/rest?version=v1');

            // Step 4: Fetch contacts using People API
            let allContacts = [];
            let nextPageToken = null;

            do {
                const response = await window.gapi.client.people.people.connections.list({
                    resourceName: 'people/me',
                    personFields: 'names,emailAddresses,phoneNumbers',
                    pageSize: 100,
                    pageToken: nextPageToken || undefined
                });

                if (response.result.connections) {
                    allContacts = allContacts.concat(response.result.connections);
                }

                nextPageToken = response.result.nextPageToken;
            } while (nextPageToken);

            if (allContacts.length === 0) {
                setIsLoading(false);
                return [];
            }

            // Process contacts to match our format (processContact expects name as array, email and tel as arrays)
            const rawContacts = allContacts.map(connection => {
                const name = connection.names?.[0]?.displayName || 
                            connection.names?.[0]?.givenName || 
                            '';
                
                const emails = (connection.emailAddresses || [])
                    .map(email => email.value)
                    .filter(Boolean);
                
                const phones = (connection.phoneNumbers || [])
                    .map(phone => phone.value)
                    .filter(Boolean);

                return {
                    name: [name], // processContact expects name as array
                    email: emails, // processContact expects email as array
                    tel: phones // processContact expects tel as array
                };
            });

            // Process and deduplicate contacts using existing utilities
            const processedContacts = rawContacts
                .map(contact => processContact(contact))
                .filter(contact => contact.normalizedEmail || contact.normalizedPhone); // Only keep contacts with email or phone

            const uniqueContacts = deduplicateContacts(processedContacts);
            
            setContacts(uniqueContacts);
            setIsLoading(false);
            
            return uniqueContacts;

        } catch (err) {
            const errorMessage = err.error?.message || err.message || 'Failed to import Google contacts';
            setError(errorMessage);
            setIsLoading(false);
            
            // If user cancelled, don't throw error
            if (err.message?.includes('cancelled') || 
                err.message?.includes('popup') ||
                err.message?.includes('user_closed')) {
                return [];
            }
            
            throw err;
        }
    }, [googleClientId, isApiLoaded]);

    /**
     * Signs out from Google (revokes token)
     */
    const signOut = useCallback(() => {
        if (accessToken && window.google?.accounts?.oauth2) {
            window.google.accounts.oauth2.revoke(accessToken);
        }
        setIsAuthenticated(false);
        setAccessToken(null);
        setContacts([]);
        if (window.gapi?.client) {
            window.gapi.client.setToken(null);
        }
    }, [accessToken]);

    return {
        contacts,
        isLoading,
        error,
        importGoogleContacts,
        signOut,
        isAuthenticated,
        isApiLoaded
    };
};

export default useGoogleContacts;

